import type { DrillDownFilter } from '@/types/filters'
import { FILTER_TYPE_LABELS } from '@/types/filters'

interface ActiveFilterBannerProps {
  filter: DrillDownFilter
  onClear: () => void
  isEmpty?: boolean
}

function getEmptyFilterMessage(filter: DrillDownFilter): string | null {
  if (filter.label === 'Unknown') {
    switch (filter.type) {
      case 'country_code':
      case 'city':
        return 'Visitors with unknown locations are typically bots, VPN users, or users with privacy extensions that block geolocation.'
      case 'browser':
      case 'os':
      case 'device_type':
        return 'Visitors with unknown device info are typically bots or automated scripts that don\'t report standard browser information.'
      default:
        return 'Sessions with unknown values may be from bots or visitors using privacy tools.'
    }
  }
  return null
}

export function ActiveFilterBanner({ filter, onClear, isEmpty }: ActiveFilterBannerProps) {
  const emptyMessage = isEmpty ? getEmptyFilterMessage(filter) : null

  return (
    <div className="space-y-2">
      <div className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-primary-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <span className="text-sm text-primary-800">
            Filtering by{' '}
            <strong>{FILTER_TYPE_LABELS[filter.type]}</strong>:{' '}
            {filter.label}
          </span>
        </div>
        <button
          onClick={onClear}
          className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center gap-1 transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          Clear filter
        </button>
      </div>
      {isEmpty && emptyMessage && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-2">
          <svg
            className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm text-amber-800">
            No data found for this filter. {emptyMessage}
          </span>
        </div>
      )}
    </div>
  )
}
